'use client';

import { c as _c } from "react/compiler-runtime";
import { createContext, useCallback, useContext, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { motion, stagger } from 'motion/react';
import { cn } from '~/utils';
import { Button } from '~/components/primitives';
import Title from '~/components/utils/Title';
import { useI18n } from '~/features/i18n';
import { ActivityCard_Skeleton } from '../ActivityCard/ActivtiyCardSkeleton';
import { useActivitySearchCtx } from '../search/client/context';

/** Responsive column count for the activity grid, given a measured container
 * width. Matches the breakpoints encoded in the CSS grid template. */
export function getResponsiveLayout(width: number): number {
  if (width >= 1100) return 4;
  if (width >= 700) return 3;
  if (width >= 500) return 2;
  return 1;
}

/** Per-breakpoint horizontal gap (px) used by the grid template — kept in
 * sync with the `gap-4 xl:gap-5` rules below. Callers that need to compute
 * absolute card dimensions (e.g. an SSR'd USP slot) can derive from this. */
export function getGridGap(width: number): number {
  return width >= 1280 ? 20 : 16;
}

/** Derived card height for a given container width and column count. Used by
 * the WEB infinite-scroll virtualizer; not needed by the simple grid below. */
export function calculateCardHeight(gridWidth: number, columns: number): number {
  const gap = getGridGap(gridWidth);
  const availableWidth = Math.max(gridWidth, 0);
  const cardWidth = (availableWidth - (columns - 1) * gap) / columns;
  const imageWidth = cardWidth - 24;
  const imageHeight = imageWidth * 285 / 365;
  return 24 + imageHeight + 75 + gap;
}
export function useWindowWidth(t0) {
  const $ = _c(2);
  const fallbackWidth = t0 === undefined ? 375 : t0;
  const [windowWidth, setWindowWidth] = useState(fallbackWidth);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const handleResize = () => setWindowWidth(window.innerWidth);
      handleResize();
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useLayoutEffect(t1, t2);
  return windowWidth;
}

/**
 * Measures the width of whichever element the returned callback ref is attached to.
 *
 * Returns `0` until the element has actually been measured. Callers that need
 * a fallback (e.g. for initial layout estimation) should write
 * `gridWidth || windowWidth`; callers that must avoid rendering with a guess
 * should gate on `gridWidth > 0`.
 *
 * Uses a callback ref (not `useRef` + a single layout effect) so the
 * `ResizeObserver` re-attaches whenever the rendered element changes — e.g.
 * when the parent component switches between a skeleton wrapper and the
 * loaded-data wrapper.
 */
export function useMeasuredWidth() {
  const $ = _c(3);
  const [width, setWidth] = useState(0);
  const observerRef = useRef(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = element => {
      observerRef.current?.disconnect();
      observerRef.current = null;
      if (!element) {
        return;
      }
      setWidth(element.getBoundingClientRect().width);
      if (typeof ResizeObserver === "undefined") {
        return;
      }
      const observer = new ResizeObserver(entries => {
        const entry = entries[0];
        if (!entry) {
          return;
        }
        setWidth(entry.contentRect.width);
      });
      observer.observe(element);
      observerRef.current = observer;
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const ref = t0;
  let t1;
  if ($[1] !== width) {
    t1 = [ref, width];
    $[1] = width;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1 as const;
}
const ActivityCardGridLayoutCtx = createContext<{
  columns: number;
  gridWidth: number;
} | null>(null);

/** Read the measured layout values of the enclosing `<ActivityCardGrid>`.
 * Children that need to position themselves relative to the column count
 * (e.g. inserting a USP card at the right edge of the first row) read this
 * inside the grid's render tree. Returns `null` outside the grid. */
export function useActivityCardGridLayout() {
  return useContext(ActivityCardGridLayoutCtx);
}
const CARD_ANIMATION_VARIANTS = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0
  }
};

/** Pure layout host for the activity card grid: responsive columns, skeleton
 * fill while loading, motion staggering on first paint. No portal/feature
 * awareness — callers render whatever they want as children (raw
 * `<ActivityCard>`s, edit-mode wrappers, USP cards, ads, etc.). */
export const ActivityCardGrid: React.FC<{
  /** When true, renders skeleton placeholders instead of children. */
  loading?: boolean;
  /** When true, applies a pulse animation over the rendered children to
   * signal a background refetch (used together with react-query's
   * `placeholderData: keepPreviousData` so the previous grid stays visible
   * instead of flashing back to skeletons on every query-key change). */
  pulsing?: boolean;
  /** Override the default skeleton count. Default: `columns × 6`. */
  skeletonCount?: number;
  className?: string;
  children?: React.ReactNode;
}> = props => {
  const $ = _c(23);
  const windowWidth = useWindowWidth();
  const [gridRef, gridWidth] = useMeasuredWidth();
  const columns = getResponsiveLayout(gridWidth || windowWidth);
  const skeletonCount = props.skeletonCount ?? columns * 6;
  let t0;
  if ($[0] !== columns || $[1] !== gridWidth) {
    t0 = {
      columns,
      gridWidth
    };
    $[0] = columns;
    $[1] = gridWidth;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const t1 = t0;
  let t2;
  if ($[3] !== props.className) {
    t2 = cn("@container min-w-0", props.className);
    $[3] = props.className;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      opacity: 0
    };
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      hidden: t3,
      visible: {
        opacity: 1,
        transition: {
          delayChildren: stagger(0.015),
          ease: "easeOut"
        }
      }
    };
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const t5 = props.pulsing && "animate-pulse";
  let t6;
  if ($[7] !== t5) {
    t6 = cn("relative mb-10 grid items-stretch gap-4", "@min-[500px]:grid-cols-2 @min-[700px]:grid-cols-3 @min-[1100px]:grid-cols-4 grid-cols-1", "@min-[500px]:auto-rows-fr", t5);
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== props.children || $[10] !== props.loading || $[11] !== skeletonCount) {
    t7 = props.loading ? Array.from({
      length: skeletonCount
    }, _temp) : props.children;
    $[9] = props.children;
    $[10] = props.loading;
    $[11] = skeletonCount;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t6 || $[14] !== t7) {
    t8 = <motion.main initial="visible" animate="visible" variants={t4} className={t6}>{t7}</motion.main>;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== gridRef || $[17] !== t2 || $[18] !== t8) {
    t9 = <div ref={gridRef} className={t2}>{t8}</div>;
    $[16] = gridRef;
    $[17] = t2;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t1 || $[21] !== t9) {
    t10 = <ActivityCardGridLayoutCtx.Provider value={t1}>{t9}</ActivityCardGridLayoutCtx.Provider>;
    $[20] = t1;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  return t10;
};

/** Base empty-state card. Same chrome (white rounded card, centered text,
 * fade-in) for every placeholder; presets below + page-level wrappers fill
 * in the copy and the call-to-action. */
export const ActivityCardGrid_EmptyState: React.FC<{
  title: React.ReactNode;
  description: React.ReactNode;
  ctaButton: React.ReactNode;
}> = props => {
  const $ = _c(8);
  let t0;
  if ($[0] !== props.title) {
    t0 = <Title size="2xl" className="mt-2">{props.title}</Title>;
    $[0] = props.title;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== props.description) {
    t1 = <p className="text-sm text-blue-500">{props.description}</p>;
    $[2] = props.description;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== props.ctaButton || $[5] !== t0 || $[6] !== t1) {
    t2 = <div className="my-4 flex animate-fade-in flex-col items-center justify-center gap-4 rounded-3xl bg-white p-6 text-center duration-200 sm:p-16">{t0}{t1}{props.ctaButton}</div>;
    $[4] = props.ctaButton;
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};

/** Preset: search/filter combination returned 0 activities. CTA clears the
 * active filters from the surrounding `ActivitySearchCtx`. */
export const ActivityCardGrid_NotFound: React.FC = () => {
  const ctx = useActivitySearchCtx();
  const filters = ctx.useFilters();
  const i18n = useI18n();
  return <ActivityCardGrid_EmptyState title={i18n._('web.activities.not_found.title', undefined, {
    message: 'No activities found..'
  })} description={i18n._('web.activities.not_found.description', undefined, {
    message: 'No activities found..'
  })} ctaButton={<Button iconBefore="close" variant="outline" onClick={filters.clear} className="[&>svg]:size-3.5">
                    {i18n._('web.activities.not_found.clear', undefined, {
      message: 'Clear filters..'
    })}
                </Button>} />;
};

/** Motion-staggered animation variants for grid children. Exported so
 * callers (e.g. WEB's per-card click-tracking wrapper) can keep their cards'
 * entrance animations in lockstep with the grid's stagger. */
export const ACTIVITY_CARD_GRID_ITEM_VARIANTS = CARD_ANIMATION_VARIANTS;
function _temp(_, i) {
  return <motion.div key={`skeleton-${i}`} style={{
    height: 334
  }} variants={CARD_ANIMATION_VARIANTS}><ActivityCard_Skeleton className="h-full" /></motion.div>;
}